import {
	Table,
	Text,
	Inline,
	Badge,
	Dropdown,
	Icons,
	Stack,
	RelativeTime,
	Button,
	AsyncSelectProps,
	AsyncSelect,
} from "@sembark-travel/ui-base"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	useSearch,
} from "@sembark-travel/ui-list"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
} from "@sembark-travel/ui-dialog"
import { TCancellationPolicyDetail, cancellationPoliciesXHR } from "./store"
import { NewCancellationPolicyInDialog } from "./NewItem"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { isTruthy, SwitchInputField } from "@sembark-travel/ui-form"
import { useEffect } from "react"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { generatePath, useNavigate } from "../router-utils"
import {
	policyFiltersToQuery,
	policyFiltersToRequestParams,
	policyQueryToFilters,
	type TPolicyListFilters,
} from "../shared/policies"

// The detail page reads the policy's name and version out of the query string,
// so every link into it has to carry the same set of params.
function cancellationPolicyDetailSearchParams(
	detail: TCancellationPolicyDetail
) {
	return new URLSearchParams({
		name: detail.name,
		is_deleted: String(Boolean(detail.deleted_at)),
		is_default: String(Boolean(detail.is_default)),
		version_id: String(detail.latest_policy.id),
		version: String(detail.latest_policy.version),
	}).toString()
}

function cancellationPolicyDetailPath(detail: TCancellationPolicyDetail) {
	const search = cancellationPolicyDetailSearchParams(detail)
	return `${generatePath("/cancellation-policy/:cancellationPolicyId", {
		cancellationPolicyId: String(detail.id),
	})}?${search}`
}

function CancellationPoliciesList() {
	const [query, setQuery] = useLocationQuery({
		toQuery: policyFiltersToQuery,
		fromQuery: policyQueryToFilters,
	})
	const [params, setParams] = useSearch<TPolicyListFilters>(query)
	const xhr = useXHR()
	const navigate = useNavigate()

	useEffect(() => {
		setQuery(params)
	}, [setQuery, params])

	return (
		<Search
			title="Cancellation Policies"
			initialParams={params}
			onSearch={(params) => {
				setParams({ ...params, page: 1 })
			}}
			Filters={Filters}
			resetParams={{
				q: "",
				page: 1,
			}}
			areAdvancedFiltersApplied={(filters) => {
				const { deleted_only, ...otherFilters } = filters
				return (
					isTruthy(deleted_only) ||
					areAdvancedFiltersAppliedDefault(otherFilters)
				)
			}}
			actions={
				<NewCancellationPolicyInDialog
					onSuccess={(data) =>
						navigate(
							{
								pathname: "/cancellation-policy/:cancellationPolicyId",
								search: cancellationPolicyDetailSearchParams(data),
							},
							{
								params: {
									cancellationPolicyId: String(data.id),
								},
							}
						)
					}
				>
					{({ onClick }) => (
						<Button status="primary" level="primary" onClick={onClick}>
							Create Policy
						</Button>
					)}
				</NewCancellationPolicyInDialog>
			}
		>
			<ListView<TCancellationPolicyDetail, TPolicyListFilters>
				pageKey="cancellation-policies"
				params={params}
				fetch={(xhr, params) => {
					return cancellationPoliciesXHR(xhr).get(
						policyFiltersToRequestParams(params)
					)
				}}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items, refresh }) => (
					<Table
						responsive
						bordered
						headers={[
							"Policy",
							...(params.deleted_only ? ["Disabled At"] : []),
							"Last Updated",
							"",
						]}
						rows={items.map((item) => [
							<Inline gap="2" alignItems="center" flexWrap="wrap">
								<Link
									to={cancellationPolicyDetailPath(item)}
									anchored
									color="accent"
									fontWeight="semibold"
								>
									{item.name}
								</Link>
								<Badge status={item.deleted_at ? "warning" : undefined}>
									v{item.latest_policy?.version}
								</Badge>
								{item.is_default ? <Badge warning>Default</Badge> : null}
							</Inline>,
							...(params.deleted_only
								? [
										<RelativeTime
											timestamp={item.deleted_at as string}
											color="warning"
										/>,
									]
								: []),
							<Stack>
								<RelativeTime timestamp={item.latest_policy.created_at} />
								<Text color="muted" fontSize="sm">
									{item.latest_policy.created_by?.name
										? `By ${item.latest_policy.created_by?.name}`
										: ""}
								</Text>
							</Stack>,
							<Inline justifyContent="end">
								{item.is_default ? null : (
									<Dropdown alignRight>
										<Dropdown.ToggleButton size="sm" level="tertiary">
											<Icons.DotsVertical />
										</Dropdown.ToggleButton>
										<Dropdown.Menu>
											<ConfirmationDialogProvider>
												{({ confirm }) => (
													<>
														<Dropdown.MenuItem
															color={item.deleted_at ? "success" : "warning"}
															onClick={async () => {
																if (await confirm()) {
																	try {
																		if (item.deleted_at) {
																			await cancellationPoliciesXHR(
																				xhr
																			).restore(item.id)
																			showSnackbar(
																				"Policy Restored Successfully"
																			)
																		} else {
																			await cancellationPoliciesXHR(
																				xhr
																			).disable(item.id)
																			showSnackbar(
																				"Policy Disabled Successfully"
																			)
																		}
																		refresh()
																	} catch (e) {
																		const error = e as Error
																		showSnackbar(error.message)
																	}
																}
															}}
														>
															{item.deleted_at ? (
																<Icons.Refresh />
															) : (
																<Icons.Ban />
															)}
															{item.deleted_at ? " Restore" : " Disable"}
														</Dropdown.MenuItem>
														<ConfirmationDialog
															title={
																item.deleted_at
																	? "Restore Policy"
																	: "Disable Policy"
															}
															destructiveAction={Boolean(!item.deleted_at)}
															acceptActionLabel={
																item.deleted_at ? "Restore" : "Disable"
															}
														>
															<Text>
																Are you sure you want to{" "}
																{item.deleted_at ? "restore" : "disable"}{" "}
																<Text as="span" fontWeight="semibold">
																	{item.name}{" "}
																</Text>
																policy ?
															</Text>
														</ConfirmationDialog>
													</>
												)}
											</ConfirmationDialogProvider>
										</Dropdown.Menu>
									</Dropdown>
								)}
							</Inline>,
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}

export function SelectCancellationPolicyDetail({
	...props
}: Omit<AsyncSelectProps, "fetch">) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			perFetchLimit={15}
			{...props}
			fetch={(q, { page }) =>
				cancellationPoliciesXHR(xhr)
					.get({ q, page, limit: 15 })
					.then((resp) => resp.data)
			}
		/>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<SwitchInputField name="deleted_only" label="Disabled Only" />
		</Stack>
	)
}

export { CancellationPoliciesList }
